import {
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getBarcodeCategories } from 'store/barcode-categories/actions';
import {
  getBarcodeCategoriesFormatted,
  getBarcodeCategoriesLoadingState,
  getBarcodeCategoriesOptions,
} from 'store/barcode-categories/selectors';
import useDebounce from 'library/hooks/use-debounce';
import { getLabelMap } from 'library/helpers/text';
import MiniSearch from 'minisearch';

// eslint-disable-next-line import/prefer-default-export
export const DEFAULT_SEARCH_CONFIGURATION = {
  fields: ['text'],
  idField: 'value',
  searchOptions: {
    prefix: true,
  },
};

export const getDefaultSearchResultMap = (result, { value }) => ({
  ...result,
  [value]: true,
}); // => { [item id]: true }

export const getSearchResultMap = (result, { id }) => ({
  ...result,
  [id]: true,
}); // => { [item id]: true }

const useBarcodeCategoryList = (skip = true, defaultSnapshot = {}) => {
  const [query, onQueryChange] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const dispatch = useDispatch();
  const data = useSelector(getBarcodeCategoriesFormatted);
  const isLoading = useSelector(getBarcodeCategoriesLoadingState);
  const options = useSelector(getBarcodeCategoriesOptions);
  const labelMapSnapshot = useRef({});
  const search = useMemo(() => new MiniSearch(DEFAULT_SEARCH_CONFIGURATION), [options]);
  const [allowedOptionMap, setAllowedOptions] = useState({});

  useEffect(() => {
    if (!isLoading && !options.length) {
      dispatch(getBarcodeCategories());
    }
  }, [isLoading, skip, options?.length]);

  useEffect(() => {
    if (options?.length > 0) {
      search.addAll(options);
      setAllowedOptions(options.reduce(getDefaultSearchResultMap, {}));
    }
  }, [options?.length > 0]);

  useEffect(() => {
    if (debouncedQuery) {
      const results = search.search(debouncedQuery);

      setAllowedOptions(results.reduce(getSearchResultMap, {}));
    } else {
      // if search input is empty, allow all options to be displayed
      setAllowedOptions(options.reduce(getDefaultSearchResultMap, {}));
    }
  }, [debouncedQuery]);

  return useMemo(() => {
    const labelMap = getLabelMap(data, {
      ...defaultSnapshot,
      ...labelMapSnapshot.current,
    });

    labelMapSnapshot.current = labelMap;

    return {
      data,
      isLoading,
      options: options.filter(({ value }) => allowedOptionMap[value]),
      labelMap,
      onQueryChange,
    };
  }, [data, defaultSnapshot]);
};

export default useBarcodeCategoryList;
